<template>
    <div>
        <l-card-headline class="tw-flex tw-justify-center tw-items-center tw-space-x-4">
            <div class="tw-flex-1">
                <template v-if="isVisitor">
                    {{ $t("profile.feedback.skillsOther", { firstname: user.firstname }) }}
                    <i
                        v-b-popover.hover.bottom="
                            serializedReportSource === 'feedback'
                                ? $t('profile.feedback.helpSkillsOtherF')
                                : $t('profile.feedback.helpSkillsOtherR')
                        "
                        class="fal fa-eye tw-ml-1 tw-text-gray-400"
                    />
                </template>
                <template v-else>
                    {{ $t("profile.feedback.skillsOwn") }}
                </template>
            </div>
        </l-card-headline>

        <l-tab-navigation :tabs="tabs" :selected.sync="selectedTab" />

        <div>
            <div class="row">
                <div class="col-md-7">
                    <l-skeleton-loader v-if="isLoading" />
                    <div v-else-if="!hasResults && !reviewCycles.length" class="tw-text-gray-400">
                        {{ $t("profile.feedback.empty") }}
                    </div>
                    <div v-else-if="hasResults">
                        <template v-if="chartType === 'list'">
                            <div
                                v-for="(skill, index) in chartData"
                                :key="index"
                                class="tw-space-y-4"
                            >
                                <a
                                    class="progress tw-block tw-group tw-cursor-pointer"
                                    :href="`#/profile/${user._id}/skill-timeline/${skill._id._id}`"
                                >
                                    <div
                                        class="progress-bar progress-bar-primary"
                                        :class="{
                                            'progress-bar-striped': isSkillHover === index,
                                            'progress-bar-success': skill.noFeedback,
                                        }"
                                        role="progressbar"
                                        tabindex="0"
                                        aria-valuenow="60"
                                        aria-valuemin="0"
                                        aria-valuemax="100"
                                        :style="`width: ${skill.results.primary.avgP}%;`"
                                        @mouseenter="isSkillHover = index"
                                        @mouseleave="isSkillHover = false"
                                        @focusin="isSkillHover = index"
                                        @focusout="isSkillHover = false"
                                    >
                                        {{ skill._id.name }}
                                        <span v-if="skill.noFeedback">{{
                                            $t("profile.feedback.notEvaluated")
                                        }}</span>
                                        <span class="tw-hidden group-hover:tw-inline-block tw-ml-1">
                                            <span class="fal fa-search-plus"></span>
                                        </span>
                                    </div>
                                </a>
                            </div>
                        </template>
                        <template v-else>
                            <l-chart
                                :options="chartSettings.options"
                                :data="chartData"
                                :colors="chartColors"
                                :height="
                                    chartType === 'radar'
                                        ? '500'
                                        : String(chartData.labels.length * 80)
                                "
                                :type="chartType"
                            />
                        </template>

                        <div class="tw-text-center tw-mt-4">
                            <l-base-button
                                v-if="serializedReportSource === 'feedback' && rights.F_STRUCTURED"
                                :href="
                                    `#/profile/${user._id}/feedback/create?to=${user._id}&type=self-structured`
                                "
                                :button-types="['primary', 'inline']"
                                data-test-class="give-feedback"
                            >
                                {{ $t("profile.feedback.btnStartSelf") }}
                            </l-base-button>
                            <l-space :size="1" />
                        </div>
                    </div>
                    <l-space :size="1" />
                    <div class="tw-flex-grow-0 tw-font-normal">
                        <l-select
                            :value.sync="form.reportSource"
                            :options="reportSourceOptions"
                            :label="``"
                            class="tw"
                            input-class="tw-h-10"
                            data-test-class="filter"
                        />
                    </div>
                </div>
                <div class="col-md-5">
                    <div if="!isLoading && reviewCycles.length">
                        <div
                            class="tw-border tw-border-solid tw-border-gray-100 tw-rounded-lg tw-p-6"
                        >
                            <div
                                v-for="(reviewCycle, index) in reviewCycles"
                                :key="reviewCycle._id"
                                :class="index < reviewCycles.length - 1 ? 'tw-mb-4' : ''"
                            >
                                <a
                                    class="tw-truncate tw-cursor-pointer tw-text-gray-900 hover:tw-text-indigo-500 focus:tw-text-indigo-500"
                                    @click.prevent="
                                        onNavigateToReview({
                                            reviewCycleId: reviewCycle._id,
                                            revieweeId: user._id,
                                        })
                                    "
                                    @keyup.enter="
                                        onNavigateToReview({
                                            reviewCycleId: reviewCycle._id,
                                            revieweeId: user._id,
                                        })
                                    "
                                >
                                    <i class="fal fa-file tw-mr-2" />{{ reviewCycle.name }}
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import api from "@/api";
import { mapGetters } from "vuex";

import LCardHeadline from "@/components/LCardHeadline";
import LTabNavigation from "@/components/LTabNavigation";
import LSkeletonLoader from "@/components/LSkeletonLoader";
import LSelect from "@/components/LSelect";
import LChart from "@/components/LChart";
import LBaseButton from "@/components/LBaseButton";
import LSpace from "@/components/LSpace";

export default {
    components: {
        LCardHeadline,
        LTabNavigation,
        LSkeletonLoader,
        LSelect,
        LChart,
        LBaseButton,
        LSpace,
    },
    props: {
        user: {
            type: Object,
            default: () => ({}),
        },
        isVisitor: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            isLoading: true,
            selectedTab: {
                id: "question-radar",
            },
            report: {},
            reviewCycles: [],
            form: {
                reportSource: "",
            },
            isSkillHover: false,
        };
    },
    computed: {
        ...mapGetters({
            currentUser: "users/current/user",
            rights: "users/current/rights",
        }),
        tabs() {
            return [
                {
                    id: "question-radar",
                    label: this.$t("reviews.admin.btnShowQuestion"),
                    iconClass: "fal fa-chart-pie",
                },
                {
                    id: "question-horizontalBar",
                    label: this.$t("reviews.admin.btnShowQuestion"),
                    iconClass: "fal fa-chart-bar",
                },
                {
                    id: "category-radar",
                    label: this.$t("reviews.admin.btnShowCategory"),
                    iconClass: "fal fa-chart-pie",
                },
                {
                    id: "category-horizontalBar",
                    label: this.$t("reviews.admin.btnShowCategory"),
                    iconClass: "fal fa-chart-bar",
                },
                {
                    id: "timeline-list",
                    label: this.$t("reviews.admin.btnShowTimeline"),
                    iconClass: "fal fa-calendar",
                    visible: this.hasResults && this.serializedReportSource === "feedback",
                },
            ];
        },
        chartType() {
            const allowedTypes = ["radar", "horizontalBar"];
            const type = this.selectedTab?.id?.split("-")?.[1];

            return allowedTypes.includes(type) ? type : "list";
        },
        chartData() {
            if (this.chartType === "list") {
                return (this.report?.results?.list || []).sort(
                    (skillA, skillB) =>
                        skillB.results?.primary?.avgP - skillA.results?.primary?.avgP,
                );
            }

            return {
                labels: this.report?.results?.graph?.labels || [],
                datasets: (this.report?.results?.graph?.results || []).map((result, index) => ({
                    label: this.chartSettings?.series?.[index] ?? "",
                    data: result,
                    borderWidth: 2,
                    ...(this.chartSettings?.datasetOverride?.[index]
                        ? this.chartSettings.datasetOverride[index]
                        : {}),
                })),
            };
        },
        chartColors() {
            return ["green", "red", "grey", "primary", "orange"];
        },
        chartSettings() {
            return {
                series: [
                    ...(this.serializedReportSource === "feedback"
                        ? ["Self-Reflection", "Feedback"]
                        : this.serializedReportSource === "reviews"
                        ? ["Self", "Peers", "Direct R.", "Manager", "All"]
                        : []),
                ],
                datasetOverride: [
                    {
                        hidden: false,
                    },
                    {
                        hidden: false,
                    },
                    {
                        hidden: this.serializedReportSource === "reviews" ? false : true,
                    },
                    {
                        hidden: this.serializedReportSource === "reviews" ? false : true,
                    },
                    {
                        hidden: true,
                    },
                ],
                options: {
                    spanGaps: true,
                    responsive: true,
                    maintainAspectRatio: false,
                    fullWidth: true,
                    layout: {
                        padding: {
                            left: 20,
                            right: 20,
                            top: 20,
                            bottom: 20,
                        },
                    },
                    legend: {
                        display: true,
                        position: "bottom",
                        labels: {
                            boxWidth: 15,
                            padding: 20,
                        },
                    },
                    animation: false,
                    elements: {
                        line: {
                            borderWidth: 3,
                        },
                        point: {
                            radius: 3,
                            pointStyle: "circle",
                        },
                    },
                    ...(this.chartType === "radar"
                        ? {
                              scale: {
                                  pointLabels: {
                                      callback: function(pointLabel) {
                                          if (pointLabel.length > 25)
                                              return pointLabel.substring(0, 24) + "...";
                                          else return pointLabel;
                                      },
                                  },
                                  ticks: {
                                      display: this.chartScaleType !== "100" ? true : false,
                                      min: 0,
                                      max: this.chartScaleType
                                          ? parseInt(this.chartScaleType, 10)
                                          : 5,
                                      beginAtZero: true,
                                      stepSize: this.chartScaleType !== "100" ? 1 : 100,
                                  },
                              },
                          }
                        : {
                              scales: {
                                  xAxes: [
                                      {
                                          display: false,
                                          ticks: {
                                              display: true,
                                              suggestedMax: this.chartScaleType
                                                  ? parseInt(this.chartScaleType, 10)
                                                  : 5,
                                              beginAtZero: true,
                                          },
                                      },
                                  ],
                              },
                          }),
                    tooltips: {
                        enabled: true,
                        mode: "label",
                        callbacks: {
                            title: function(tooltipItems, data) {
                                var idx = tooltipItems[0].index;
                                return data.labels[idx];
                            },
                            ...(this.chartScaleType === "100"
                                ? {
                                      label: function(tooltipItems, data) {
                                          return data.datasets[tooltipItems.datasetIndex].label;
                                      },
                                  }
                                : {}),
                        },
                    },
                },
            };
        },
        chartScaleType() {
            return this.report?.settings?.scale?.type;
        },
        hasResults() {
            return this.report?.noResults === false;
        },
        reportSourceOptions() {
            const options = [];

            if (this.currentUser.rights.F_STRUCTURED) {
                options.push({
                    text: this.$t("profile.feedback.optionShowFeedback"),
                    value: "feedback",
                });
            }

            if (this.currentUser.rights.R_VIEW) {
                options.push({
                    text: this.$t("profile.feedback.optionShowAllReviews"),
                    value: "reviews",
                });
            }

            for (const reviewCycle of this.reviewCycles) {
                options.push({ text: reviewCycle.name, value: reviewCycle._id });
            }

            return options;
        },
        serializedReportSource() {
            return ["feedback", "reviews"].includes(this.form.reportSource)
                ? this.form.reportSource
                : "reviews";
        },
        reportQuery() {
            const isForSingleReview =
                !["feedback", "reviews"].includes(this.form.reportSource) && this.form.reportSource;

            return {
                mode: "byParticipant",
                viewAs: this.isVisitor ? "owner" : "reviewee",
                format: "snapshot",
                export: false,
                source: this.serializedReportSource,
                filters: {
                    primary: {
                        users: [{ _id: this.user._id }],
                        managers: [],
                        localTeams: [],
                        perspectives: [],
                        skills: [],
                        categories: [],
                        reviewCycles: [
                            ...(isForSingleReview ? [{ _id: this.form.reportSource }] : []),
                        ],
                        periods: [],
                    },
                    secondary: {
                        users: [],
                        managers: [],
                        localTeams: [],
                        perspectives: [],
                        skills: [],
                        categories: [],
                        reviewCycles: [],
                        periods: [],
                    },
                },
                enableSecondaryFilters: false,
                groupBy: "localTeam",
                dimensionType: this.selectedTab?.id?.startsWith("category") ? "category" : "skill",
                dimensionShowDeactivated: isForSingleReview ? true : false,
                dimensionIncludeCustomQuestions: false,
                scatter: {
                    x: {},
                    y: {},
                },
                ignoreSafeguard: true,
                allowFallbackToFeedback: true,
            };
        },
    },
    watch: {
        reportQuery: {
            immediate: true,
            deep: true,
            async handler(newValue) {
                await this.loadData(newValue);
            },
        },
        serializedReportSource: {
            deep: true,
            handler(newValue) {
                if (this.selectedTab?.id === "timeline-list" && newValue !== "feedback") {
                    this.selectedTab = this.tabs[0];
                }
            },
        },
    },
    created() {
        if (this.currentUser.rights.R_VIEW) {
            this.form.reportSource = "reviews";
        } else {
            this.form.reportSource = "feedback";
        }
    },
    methods: {
        async onNavigateToReview({
            reviewCycleId,
            revieweeId,
            role = "manager",
            viewAs = "owner",
            entrypoint = "toDo",
        } = {}) {
            // get participant status
            const { data } = await api.get(
                `/api/reviewcycles/get/details/${reviewCycleId}/reviewee/${revieweeId}/${role}/${this.currentUser._id}/viewAs/${viewAs}`,
            );

            if (data.redirect) {
                this.$router.push(
                    `/reviews/update/${reviewCycleId}/reviewee/${revieweeId}/${data.redirect}/${this.currentUser._id}/viewAs/${data.redirect}/overview/${entrypoint}`,
                );
            } else {
                this.$router.push(
                    `/reviews/update/${reviewCycleId}/reviewee/${revieweeId}/${role}/${this.currentUser._id}/viewAs/${viewAs}/overview/${entrypoint}`,
                );
            }
        },
        async loadData(filters = {}) {
            // wait for data to be set
            if (!this.form.reportSource) return;

            this.isLoading = true;

            try {
                const { data } = await api.post("/api/stats/get/report", filters);
                this.report = data;

                // set and keep data once available as not all report request payloads contain that data
                if (
                    Array.isArray(this.report?.settings?.reviewCyclesWithAccess) &&
                    this.report.settings.reviewCyclesWithAccess.length &&
                    this.form.reportSource === "reviews"
                ) {
                    this.reviewCycles = this.report.settings.reviewCyclesWithAccess;
                }
            } finally {
                this.isLoading = false;
            }
        },
    },
};
</script>
